import { MAX_LEVEL } from '../balance';
import { xpToNext } from '../../game/character';
import type { QuestDef, QuestObjective, QuestRewards, QuestStep } from '../../game/quests';

/**
 * Builders for quest content. Objectives carry the zone and position of the
 * thing to do so the minimap, the map panel (M) and the journal can point at
 * it; `quests.test.ts` checks every position against the zone layouts.
 */

// ---------------------------------------------------------------- NPC placements

/** Where each quest NPC stands (mirrors the zone layouts; verified by tests). */
export const NPC_AT: Record<string, { zone: string; x: number; z: number }> = {
  captain_brenna: { zone: 'emberwild', x: -8, z: 80 },
  quartermaster_odo: { zone: 'emberwild', x: 5, z: 86 },
  scout_lyra: { zone: 'emberwild', x: -14, z: 88 },
  high_warden_aldara: { zone: 'havenreach', x: 0, z: -58 },
  smith_borin: { zone: 'havenreach', x: -45, z: 45 },
  merchant_tessa: { zone: 'havenreach', x: 32.6, z: 0.2 },
  bounty_master_quill: { zone: 'havenreach', x: 49, z: -32.5 },
  broker_venn: { zone: 'havenreach', x: 64.6, z: 5.9 },
  rift_scholar_elowen: { zone: 'havenreach', x: 41.5, z: -46.5 },
  harbormaster_sol: { zone: 'havenreach', x: -50.5, z: -38 },
  card_collector_mira: { zone: 'havenreach', x: 13.9, z: -5.7 },
  sigil_scribe_orin: { zone: 'havenreach', x: -13.9, z: -5.7 },
  gemcutter_ysa: { zone: 'havenreach', x: 13.9, z: 5.7 },
  minstrel_lio: { zone: 'havenreach', x: 4.5, z: 8.5 },
  keeper_marn: { zone: 'havenreach', x: -13.9, z: 5.7 },
  caravan_master_hadi: { zone: 'sunscar', x: -62, z: -19.5 },
  nomad_seer_zahra: { zone: 'sunscar', x: -69, z: -11.5 },
  trader_farouk: { zone: 'sunscar', x: -55, z: -11.5 },
  abbess_vira: { zone: 'frosthollow', x: -52, z: 5 },
  hunter_bjorn: { zone: 'frosthollow', x: -71, z: 22 },
  trader_olga: { zone: 'frosthollow', x: -59, z: 15.5 },
  steward_pim: { zone: 'haven', x: 4.5, z: -14.5 },
};

/** Zone centre and a radius covering the whole map ("arrive in …" objectives). */
const ZONE_EXTENT: Record<string, { x: number; z: number; r: number }> = {
  emberwild: { x: 0, z: 0, r: 160 },
  havenreach: { x: 0, z: 0, r: 135 },
  sunscar: { x: 0, z: 0, r: 175 },
  frosthollow: { x: 0, z: 0, r: 175 },
  sea: { x: 0, z: 0, r: 430 },
  haven: { x: 0, z: 0, r: 125 },
  glimmer: { x: 0, z: 0, r: 160 },
  rift_1: { x: 0, z: 0, r: 80 },
};

// ---------------------------------------------------------------- objectives

export function kill(
  target: string,
  count: number,
  text: string,
  zone?: string,
  x?: number,
  z?: number,
  radius = 12,
): QuestObjective {
  return {
    type: 'kill',
    target,
    count,
    text,
    ...(zone !== undefined && x !== undefined && z !== undefined
      ? { zone, pos: { x, z }, radius }
      : {}),
  };
}

export function talk(npc: string, text: string): QuestObjective {
  const at = NPC_AT[npc];
  return {
    type: 'talk',
    target: npc,
    count: 1,
    text,
    ...(at ? { zone: at.zone, pos: { x: at.x, z: at.z }, radius: 3 } : {}),
  };
}

export function reach(
  zone: string,
  x: number,
  z: number,
  radius: number,
  text: string,
): QuestObjective {
  return {
    type: 'reach',
    target: `${zone}:${x},${z}`,
    count: 1,
    text,
    zone,
    pos: { x, z },
    radius,
  };
}

/** Arrive anywhere in a zone (fires on the zone's arrival `reach` event). */
export function arrive(zone: string, text: string): QuestObjective {
  const e = ZONE_EXTENT[zone];
  if (!e) throw new Error(`No extent for zone '${zone}'`);
  return {
    type: 'reach',
    target: `${zone}:*`,
    count: 1,
    text,
    zone,
    pos: { x: e.x, z: e.z },
    radius: e.r,
  };
}

/** Use (G) a world interactable: lore tablet, waystone, chest, vista or heart. */
export function interact(
  target: string,
  text: string,
  zone: string,
  x: number,
  z: number,
): QuestObjective {
  return { type: 'interact', target, count: 1, text, zone, pos: { x, z }, radius: 3 };
}

/** Clear a story dungeon; the marker sits on its entrance in the field zone. */
export function clearDungeon(
  dungeon: string,
  text: string,
  zone: string,
  x: number,
  z: number,
): QuestObjective {
  return {
    type: 'interact',
    target: `clear_${dungeon}`,
    count: 1,
    text,
    zone,
    pos: { x, z },
    radius: 4,
  };
}

/** Pick up any Glimmerseed (the game fires `interact: seed` for each one). */
export function seeds(count: number, text: string, zone: string): QuestObjective {
  return { type: 'interact', target: 'seed', count, text, zone };
}

export function collect(
  item: string,
  count: number,
  text: string,
  zone?: string,
  x?: number,
  z?: number,
  radius = 30,
): QuestObjective {
  return {
    type: 'collect',
    target: item,
    count,
    text,
    ...(zone !== undefined && x !== undefined && z !== undefined
      ? { zone, pos: { x, z }, radius }
      : {}),
  };
}

/** A character flag; tutorial flags are derived from game state by the quests feature. */
export function flag(target: string, text: string): QuestObjective {
  return { type: 'flag', target, count: 1, text };
}

export function step(hint: string | undefined, ...objectives: QuestObjective[]): QuestStep {
  return hint ? { objectives, hint } : { objectives };
}

// ---------------------------------------------------------------- rewards

const round10 = (n: number): number => Math.max(10, Math.round(n / 10) * 10);

/** XP worth `share` of the level-up at `level` (level-cap quests use the last level-up). */
export const questXp = (level: number, share: number): number =>
  round10(xpToNext(Math.min(level, MAX_LEVEL - 1)) * share);
export const mainSilver = (level: number): number => round10(60 + 45 * level);
export const sideSilver = (level: number): number => round10(30 + 24 * level);

export function mainRewards(
  level: number,
  share: number,
  extra: Omit<QuestRewards, 'xp'> = {},
): QuestRewards {
  return { xp: questXp(level, share), silver: mainSilver(level), ...extra };
}

export function sideRewards(
  level: number,
  share: number,
  extra: Omit<QuestRewards, 'xp'> = {},
): QuestRewards {
  return { xp: questXp(level, share), silver: sideSilver(level), ...extra };
}

export const items = (...list: [string, number][]): { id: string; count: number }[] =>
  list.map(([id, count]) => ({ id, count }));

// ---------------------------------------------------------------- quest shells

type QuestInput = Omit<QuestDef, 'kind' | 'dialogue'> & {
  offer: string[];
  progress?: string;
  complete: string[];
};

function build(kind: QuestDef['kind'], q: QuestInput): QuestDef {
  const { offer, progress, complete, ...rest } = q;
  return {
    kind,
    ...rest,
    dialogue: progress ? { offer, progress, complete } : { offer, complete },
  };
}

export const main = (q: QuestInput): QuestDef => build('main', q);
export const side = (q: QuestInput): QuestDef => build('side', q);
export const world = (q: QuestInput): QuestDef => build('world', q);
